import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useWatch } from "react-hook-form";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  InputLabel,
  TextField,
} from "@mui/material";
import { useGetApiElaboranetTargets, useGetNodo } from "@/api";
import { Control } from "react-hook-form";
import { NodoCreateOrUpdateDto, NodoDto, NodoTipo } from "@/api";
import { portaleConfig } from "@/portaliConfig";

type NodoDialogSectionElaboranetProps = {
  nodo?: NodoDto | null;
  padre?: NodoDto | null;
  control: Control<NodoCreateOrUpdateDto>;
};

export default function NodoDialogSectionElaboranet({
  nodo,
  padre,
  control,
}: NodoDialogSectionElaboranetProps) {
  const { t } = useTranslation();
  const tipoNodo = useWatch({
    control: control,
    name: "tipo",
  });

  const aziendaId = useMemo(() => {
    if (nodo?.breadcrumbs && nodo.breadcrumbs.length > 0) {
      return nodo.breadcrumbs[0].nodo.id;
    } else if (padre?.tipo === "Azienda") {
      return padre.id;
    } else if (padre?.breadcrumbs) {
      return padre.breadcrumbs[0].nodo.id;
    } else {
      return null;
    }
  }, [nodo, padre]);

  const nodoAzienda = useGetNodo(aziendaId ?? 0, {
    query: {
      enabled: !!aziendaId,
    },
  });

  const { data: _targetsElaboranet } = useGetApiElaboranetTargets({
    query: {
      enabled: tipoNodo === "Azienda" || tipoNodo === "Contenitore",
    },
  });

  const aziendaElaboranetClienteCodice =
    nodoAzienda.data?.data?.elaboranetTarget?.codiceCliente;

  const targetsElaboranet = useMemo(() => {
    if (tipoNodo !== "Azienda" && tipoNodo !== "Contenitore") {
      return null;
    } else if (!_targetsElaboranet?.data) {
      // caricamento in corso ...
      return [];
    }

    if (tipoNodo === "Azienda") {
      return _targetsElaboranet.data.filter((x) => x.tipo === "cliente");
    } else {
      if (aziendaElaboranetClienteCodice) {
        return _targetsElaboranet.data.filter(
          (x) =>
            x.tipo === "centrale" &&
            x.codiceCliente === aziendaElaboranetClienteCodice
        );
      } else {
        return null;
      }
    }
  }, [_targetsElaboranet, tipoNodo, aziendaElaboranetClienteCodice]);

  const elaboranetTargetId = useWatch({
    control,
    name: "elaboranetTargetId",
  });

  return (
    <Box>
      <InputLabel htmlFor="nodo-centrale-elaboranet">
        {tipoNodo === "Azienda"
          ? t("nodoDialog.elaboranet.labelCliente")
          : t("nodoDialog.elaboranet.labelCentrale")}
      </InputLabel>
      {targetsElaboranet === null ? (
        <Alert severity="warning">
          {t("nodoDialog.elaboranet.avvisoSelezione")}
        </Alert>
      ) : (
        <Controller
          name="elaboranetTargetId"
          control={control}
          render={({ field }) => {
            return (
              <Autocomplete
                {...field}
                options={targetsElaboranet}
                getOptionLabel={(option) => {
                  return option.nome;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="nodo-centrale-elaboranet"
                    fullWidth
                  />
                )}
                onChange={(_, value) => {
                  if (value) {
                    field.onChange(value.id);
                  } else {
                    field.onChange(null);
                  }
                }}
                value={
                  targetsElaboranet.find((x) => x.id === field.value) ?? null
                }
                isOptionEqualToValue={(option, value) => {
                  return option.id === value?.id;
                }}
              />
            );
          }}
        />
      )}

      <Box sx={{ pl: 1, pt: 1 }}>
        <Controller
          name="analisiInviaAlCommerciale"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  disabled={!elaboranetTargetId}
                />
              }
              label={t("nodoDialog.elaboranet.inviaCommerciale")}
            />
          )}
        />
        <Controller
          name="analisiInviaAlAreaManager"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  disabled={!elaboranetTargetId}
                />
              }
              label={t("nodoDialog.elaboranet.inviaAreaManager")}
            />
          )}
        />
      </Box>
    </Box>
  );
}
